import { useEffect, useMemo } from "react";
import { useAlbums } from "common/hooks";
import type { Album, AlbumsByType, Photo } from "common/apis/onboarding";

/**
 * Onboarding's CMS albums, one per colour mode. Addressed by name rather than
 * id, as the Learning Hub's lessons are: the name is agreed up front, the id is
 * whatever each environment's CMS gave it.
 */
export const FEATURE_INTRO_ALBUMS = {
  light: "Feature intro (Light)",
  dark: "Feature intro (Dark)",
} as const;

export const SHOW_ME_AROUND_ALBUMS = {
  light: "Onboarding Static - Show me around (Light)",
  dark: "Onboarding Static - Show me around (Dark)",
} as const;

/**
 * Looked for across every album type: the two pairs above are filed under
 * different ones ("home", "onboarding"). Matched case-insensitively and
 * trimmed, because the name is typed into the CMS by hand.
 */
const byName = (albums: AlbumsByType | undefined, name: string) => {
  const wanted = name.trim().toLowerCase();
  return ([] as Album[])
    .concat(...Object.values(albums ?? {}))
    .find((album) => album.name?.trim().toLowerCase() === wanted);
};

/** The album's pictures in `position` order, which is the slides' order. */
const orderedImageUrls = (album?: Album): (string | undefined)[] =>
  [...(album?.photos ?? [])]
    .sort((a: Photo, b: Photo) => a.position - b.position)
    .map((photo) => photo.image_url ?? undefined);

/**
 * A light/dark pair of albums as picture URLs, indexed like the caller's
 * slides.
 *
 * The album rows carry no id of their own, so they pair with the slides by
 * position — the same pairing native's `WhatYouCanAlsoDoPage` makes.
 *
 * Once the albums are in, the visible colour mode's pictures are fetched up
 * front, so moving between slides rarely waits on the network. Only that
 * mode: each picture is several hundred kilobytes.
 */
export function useAlbumArtwork(names: { light: string; dark: string }) {
  const { albums, isLoading } = useAlbums("desktop");

  const artwork = useMemo(
    () => ({
      light: orderedImageUrls(byName(albums, names.light)),
      dark: orderedImageUrls(byName(albums, names.dark)),
    }),
    [albums, names.light, names.dark],
  );

  useEffect(() => {
    // The same test `AlbumArtwork`'s dark `<source>` makes.
    const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const urls =
      isDark && artwork.dark.length > 0 ? artwork.dark : artwork.light;
    urls.forEach((url) => {
      if (url) new Image().src = url;
    });
  }, [artwork]);

  return { ...artwork, isLoading };
}
